/*  features/orderDetails/orderDetailsActions.js  */
import {createAsyncThunk} from '@reduxjs/toolkit';

import {
    fetchInvoiceData,
    fetchOrderDetails,
    fetchOrderStatus,
    submitFinishingOptions as apiSubmitFinishingOptions,
    updateOrderOptions as apiUpdateOrderOptions
} from '../../components/CustomerPortalComponent/OrderDetails/utils/api';
import {isUserAuthorized} from "../../components/CustomerPortalComponent/OrderDetails/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Thunks                                                             */
/* ------------------------------------------------------------------ */

/**
 * Pull everything needed for the screen once.
 */
// export const fetchOrderPageData = createAsyncThunk(
//     'orderDetails/fetchOrderPageData',
//     async ({orderId, token, currentUser}, {rejectWithValue}) => {
//         try {
//             const [orderRes, statusRes, invoiceRes] = await Promise.all([
//                 fetchOrderDetails(orderId, token),
//                 fetchOrderStatus(orderId, token),
//                 // first pass: no options yet, that’s fine
//                 fetchInvoiceData(orderId, token, {})
//             ]);
//
//             if (!isUserAuthorized(currentUser, orderRes.data)) {
//                 return rejectWithValue({orderId, reason: 'unauthorized'});
//             }
//
//             return {
//                 orderId,
//                 order: orderRes.data,
//                 status: statusRes.data,
//                 invoiceData: invoiceRes.data
//             };
//         } catch (err) {
//             if (err.response && err.response?.status === 404) {
//                 return rejectWithValue({
//                     orderId,
//                     reason:
//                         "Sorry. Your order is still being processed. You will receive an email when your order has been processed."
//                 });
//             }
//
//             return rejectWithValue({orderId, reason: err.message});
//         }
//     },
//     {
//         condition: ({orderId}, {getState}) =>
//             !getState().orderDetails.byId[orderId]          // skip if cached
//     }
// );


const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

export const fetchOrderPageData = createAsyncThunk(
    'orderDetails/fetchOrderPageData',
    async ({orderId, token, currentUser}, {rejectWithValue}) => {
        try {
            const [orderRes, statusRes, invoiceRes] = await Promise.all([
                fetchOrderDetails(orderId, token),
                fetchOrderStatus(orderId, token),
                fetchInvoiceData(orderId, token, {}),
            ]);

            if (!isUserAuthorized(currentUser, orderRes.data)) {
                return rejectWithValue({orderId, reason: 'unauthorized'});
            }

            return {
                orderId,
                order: orderRes.data,
                status: statusRes.data,
                invoiceData: invoiceRes.data,
            };
        } catch (err) {
            if (err.response?.status === 404) {
                return rejectWithValue({
                    orderId,
                    reason:
                        "Sorry. Your order is still being processed. You will receive an email when your order has been processed."
                });
            }
            return rejectWithValue({orderId, reason: err.message});
        }
    },
    {
        condition: ({orderId}, {getState}) => {
            const entry = getState().orderDetails.byId[orderId];

            // never fetched before → fetch
            if (!entry) {
                return true;
            }

            // last fetch errored → fetch immediately
            if (entry.error) {
                return true;
            }

            // success & still fresh → skip
            if (Date.now() - (entry.fetchedAt || 0) < CACHE_TTL) {
                return false;
            }

            // otherwise stale → fetch
            return true;
        }
    }
);


/**
 * Toggle hardware / assembly / etc. options.
 */
export const updateOrderOptions = createAsyncThunk(
    'orderDetails/updateOrderOptions',
    async ({orderId, token, options}, {rejectWithValue}) => {
        try {
            const res = await apiUpdateOrderOptions(orderId, token, options);
            return {orderId, invoiceData: res.data, options};
        } catch (err) {
            return rejectWithValue({orderId, reason: err.message});
        }
    }
);

/**
 * Save finishing type / colour and refresh invoice.
 */
export const submitFinishing = createAsyncThunk(
    'orderDetails/submitFinishing',
    async ({orderId, token, finishingType, finishingColor}, {rejectWithValue}) => {
        try {
            await apiSubmitFinishingOptions(orderId, token, {finishingType, finishingColor});
            // fetch updated invoice (same helper you already have)
            const invoiceRes = await fetchInvoiceData(orderId, token, {});
            return {orderId, finishingType, finishingColor, invoiceData: invoiceRes.data};
        } catch (err) {
            return rejectWithValue({orderId, reason: err.message});
        }
    }
);
